import { Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../core/auth.service';
import { errorMessage } from '../core/http-error';
import { LanguageService } from '../core/language';
import { PHONE_COUNTRIES, flag } from './phone-countries';

function realBirth(value: string): boolean {
  const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(value);
  if (!match) {
    return false;
  }
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return false;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return year >= 1900 && date.getTime() <= today.getTime();
}

@Component({
  selector: 'app-details',
  imports: [ReactiveFormsModule],
  templateUrl: './details.html',
})
export class DetailsPage {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  protected readonly lang = inject(LanguageService);
  protected readonly countries = PHONE_COUNTRIES;
  protected readonly flag = flag;
  protected readonly submitting = signal(false);
  protected readonly serverError = signal<string | null>(null);

  protected readonly form = inject(FormBuilder).nonNullable.group({
    address: ['', [Validators.required, Validators.maxLength(160)]],
    city: ['', [Validators.required, Validators.maxLength(80)]],
    postalCode: ['', [Validators.required, Validators.maxLength(20)]],
    residence: ['', Validators.required],
    nationality: ['', Validators.required],
    birth: ['', [Validators.required, Validators.pattern(/^\d{2}\.\d{2}\.\d{4}$/)]],
  });

  constructor() {
    this.auth.ensureUser().subscribe((user) => {
      if (!user) {
        void this.router.navigate(['/signin'], { queryParams: { returnUrl: '/details' } });
        return;
      }
      if (!user.detailsPending) {
        void this.router.navigateByUrl('/portfolio');
        return;
      }
      const home = this.countries.find((item) => item.name === user.country);
      if (home && !this.form.controls.residence.value) {
        this.form.controls.residence.setValue(home.iso);
      }
    });
  }

  protected maskBirth(event: Event): void {
    const digits = (event.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 8);
    let text = digits.slice(0, 2);
    if (digits.length > 2) {
      text += '.' + digits.slice(2, 4);
    }
    if (digits.length > 4) {
      text += '.' + digits.slice(4, 8);
    }
    this.form.controls.birth.setValue(text);
  }

  protected submit(): void {
    const birthOk = realBirth(this.form.controls.birth.value);
    if (!birthOk) {
      this.form.controls.birth.setErrors({ birth: true });
    }
    if (this.form.invalid || !birthOk) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitting.set(true);
    this.serverError.set(null);
    const value = this.form.getRawValue();
    this.auth
      .saveDetails({
        address: value.address.trim(),
        city: value.city.trim(),
        postalCode: value.postalCode.trim(),
        residence: value.residence,
        nationality: value.nationality,
        dateOfBirth: value.birth,
      })
      .subscribe({
        next: () => void this.router.navigateByUrl('/portfolio'),
        error: (error: unknown) => {
          this.submitting.set(false);
          this.serverError.set(errorMessage(error, this.lang.t('detBirthErr')));
        },
      });
  }
}
